/**
 * Reports (S80), the map (S81) and the day-90 read (S82). The grid is drawn from @ff/rank-map's pure functions
 * as plain elements (no innerHTML, no Leaflet, nothing third party) with the accessible table beside it. Data
 * colours: top three green, 4 to 10 amber, 11 to 20 terracotta, not found grey. Lime is never data.
 *
 * How a number reads (a gap is never a zero, a change is a count before it's a percentage) and how two maps compare
 * point by point live in lib/report.ts as pure functions, tested in lib/report.test.ts.
 */
import { Button, Card, Chip, EmptyState, Icon, List, Row, Segmented, SelectField, Stat } from '@ff/app-ui';
import { band, type Band, type RankGridData } from '@ff/rank-map/grid';
import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { getTransport, type Res } from '../lib/api.ts';
import { send, useQuery } from '../lib/data.ts';
import { dayLabel, longDate, reportLabel, shortDate } from '../lib/format.ts';
import {
  CHANGE_SYMBOL,
  changeCells,
  changeCounts,
  changeSummary,
  changeText,
  changeTrend,
  day90Chip,
  groupNumbers,
  lastCycleValue,
  numberValue,
  points,
  rowsOf,
  type ChangeCell,
} from '../lib/report.ts';
import { location, scoped } from '../lib/router.ts';
import { useStore } from '../lib/store.ts';
import { toast, tz } from '../lib/ui.ts';
import { S } from '@ff/app-ui/copy';
import { Info, Loaded, Screen, Section } from '../ui/common.tsx';
import { LedgerRow } from './home.tsx';

type ReportData = Res<'report'>;
type ReportNumber = NonNullable<ReportData['enquiries']>;
type Movement = NonNullable<ReportData['mapMovement']>;
type MapsData = Res<'reportMaps'>;
type MapService = MapsData['services'][number];
type MapSnapshot = NonNullable<MapService['baseline']>;
type MapView = 'baseline' | 'latest' | 'change';

export function ReportsScreen(): JSX.Element {
  const q = useQuery('reports');
  return (
    <Screen title={S.reports.title} back="/account" lead={S.reports.lead} query={q}>
      <Loaded q={q}>
        {(d) => (
          <div class="stack">
            {d.nextReportOn && <p>{d.items.length ? S.reports.next(dayLabel(d.nextReportOn)) : S.reports.first(dayLabel(d.nextReportOn))}</p>}
            {d.items.length > 0 ? (
              <List>
                {d.items.map((r) => (
                  <li key={r.id}>
                    <Row href={`/reports/${r.month}`} lead={<Icon name="file" size={22} />} title={reportLabel(r.month, r.period)} meta={r.headline ?? ''} />
                  </li>
                ))}
              </List>
            ) : (
              <EmptyState title={S.reports.title} body={d.nextReportOn ? S.reports.first(dayLabel(d.nextReportOn)) : S.reports.lead} />
            )}
            <div class="btn-row">
              <Button href="/reports/map" icon="map">{S.reports.mapTitle}</Button>
              <Button href="/reports/day-90" icon="flag">{S.reports.day90}</Button>
            </div>
          </div>
        )}
      </Loaded>
    </Screen>
  );
}

/** Up, held or down against day one: words and a shape, never colour alone. */
function MovementChip({ movement }: { movement: Movement }): JSX.Element {
  return (
    <Chip tone={movement === 'up' ? 'ok' : movement === 'down' ? 'warn' : 'neutral'} icon={movement === 'up' ? 'arrow-up' : movement === 'down' ? 'arrow-down' : 'dot'}>
      {S.reports.movement[movement]}
    </Chip>
  );
}

/** What the number is, where it's from and when it was last read: the sheet behind its "i". */
function NumberInfo({ n, zone }: { n: ReportNumber; zone: string }): JSX.Element {
  return <Info title={n.label} lines={[...(n.note ? [n.note] : []), S.app.whereFrom(S.sources[n.source]), ...(n.updatedAt ? [S.app.updated(shortDate(n.updatedAt, zone))] : [])]} />;
}

/** Last cycle and the change, in words. Nothing at all when there's nothing true to say. */
function NumberCompare({ n }: { n: ReportNumber }): JSX.Element | null {
  const last = lastCycleValue(n);
  const change = changeText(n);
  const trend = changeTrend(n);
  if (last === null && change === null) return null;
  return (
    <p class="num-compare t-small t-muted">
      {last !== null && <span>{S.reports.lastCycle(last)}</span>}
      {change !== null && (
        <span class="num-change">
          {trend === 'up' && <Icon name="arrow-up" size={16} />}
          {trend === 'down' && <Icon name="arrow-down" size={16} />}
          {change}
        </span>
      )}
    </p>
  );
}

/** The headline number: enquiries, big, with its label, last cycle and the change. */
function HeadlineNumber({ n, zone }: { n: ReportNumber; zone: string }): JSX.Element {
  const v = numberValue(n);
  return (
    <Card as="section" labelledBy="r-enquiries">
      <div class="num-head">
        <h2 id="r-enquiries" class="num-label">{n.label}</h2>
        <NumberInfo n={n} zone={zone} />
      </div>
      {v.measured ? (
        <p class="num-now">
          <span class="t-figure">{v.text}</span> <span class="t-small t-muted">{S.reports.thisCycle}</span>
        </p>
      ) : (
        <p class="num-gap">{v.text}</p>
      )}
      <NumberCompare n={n} />
    </Card>
  );
}

function NumberRow({ n, zone }: { n: ReportNumber; zone: string }): JSX.Element {
  const v = numberValue(n);
  return (
    <li class="num-row">
      <div class="num-head">
        <span class="num-label">{n.label}</span>
        <NumberInfo n={n} zone={zone} />
      </div>
      {v.measured ? (
        <p class="num-now">
          <span class="t-data">{v.text}</span> <span class="t-small t-muted">{S.reports.thisCycle}</span>
        </p>
      ) : (
        <p class="num-gap">{v.text}</p>
      )}
      <NumberCompare n={n} />
    </li>
  );
}

function Bullets({ title, items, id }: { title: string; items: string[]; id: string }): JSX.Element | null {
  if (!items.length) return null;
  return (
    <Section title={title} id={id}>
      <ul class="bullets">
        {items.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </Section>
  );
}

export function ReportScreen({ month }: { month: string }): JSX.Element {
  const zone = useStore(tz);
  const q = useQuery('report', { params: { month } });
  return (
    <Screen title={reportLabel(month, q.data?.period)} back="/reports" query={q}>
      <Loaded q={q} skeleton="cards">
        {(r) => {
          const numbers = r.numbers ?? [];
          const groups = groupNumbers(numbers);
          const maps = r.maps ?? [];
          const counts = r.whatWeDidCounts ?? [];
          // A report from before the proof report (r26) has no enquiries and no numbers: it keeps its old blocks.
          const older = !r.enquiries && numbers.length === 0;
          const mapInfo = r.info.map ? <Info title={S.reports.mapTitle} lines={[r.info.map]} /> : null;
          return (
            <div class="stack" data-gap="l">
              <Card tone="frame">
                {r.period && <p class="t-small t-muted">{S.reports.covers(r.period.label)}</p>}
                <p class="t-small t-muted">{S.reports.headline}</p>
                <p class="t-heading report-headline">{r.headline}</p>
                {r.mapMovement && (
                  <p class="report-movement" data-movement={r.mapMovement}>
                    <MovementChip movement={r.mapMovement} /> <span>{S.reports.movementLine[r.mapMovement]}</span>{' '}
                    <Info title={S.reports.movementTitle} lines={[S.reports.movementInfo]} />
                  </p>
                )}
              </Card>
              {r.enquiries && <HeadlineNumber n={r.enquiries} zone={zone} />}
              {maps.length > 0 ? (
                <Section title={S.reports.mapTitle} id="r-map" info={mapInfo}>
                  {r.overall && (
                    <p>
                      {S.reports.overall(points(r.overall.top3Points))}
                      {r.overall.baselinePoints !== null ? ` ${S.reports.overallDayOne(points(r.overall.baselinePoints))}` : ''}
                    </p>
                  )}
                  <ul class="map-cards">
                    {maps.map((m) => (
                      <li key={m.serviceSlug}>
                        <Card class="map-card">
                          <p class="map-card-key">"{m.keyword}"</p>
                          <GridFromUrl url={m.pointsUrl} compact />
                          <p>{S.reports.topThreeOn(points(m.top3Points))}</p>
                          {m.baselinePoints !== null && <p class="t-small t-muted">{S.reports.dayOneWas(points(m.baselinePoints))}</p>}
                          {m.movement && (
                            <p>
                              <MovementChip movement={m.movement} />
                            </p>
                          )}
                        </Card>
                      </li>
                    ))}
                  </ul>
                  <Button href="/reports/map" variant="secondary" iconEnd="arrow-right">{S.home.mapSee}</Button>
                </Section>
              ) : (
                r.map && (
                  <Section title={S.reports.mapTitle} id="r-map" info={mapInfo}>
                    <GridFromUrl url={r.map.pointsUrl} />
                    <p>{S.reports.mapOf(r.map.top3Points)}{r.map.dayOnePoints !== null ? `. ${S.home.mapDayOne(r.map.dayOnePoints)}` : '.'}</p>
                    <Button href="/reports/map" variant="secondary" iconEnd="arrow-right">{S.home.mapSee}</Button>
                  </Section>
                )
              )}
              {groups.length > 0 && (
                <Section title={S.reports.numbers} id="r-numbers" info={<Info title={S.reports.numbers} lines={[r.info.numbers ?? S.reports.numbersInfo]} />}>
                  <div class="stack" data-gap="m">
                    {groups.map((g) => (
                      <div key={g.group} class="num-group">
                        <h3 id={`r-num-${g.group}`} class="num-group-title">{S.reports.groups[g.group]}</h3>
                        <ul class="num-list" aria-labelledby={`r-num-${g.group}`}>
                          {g.items.map((n) => (
                            <NumberRow key={n.key} n={n} zone={zone} />
                          ))}
                        </ul>
                        {g.group === 'reviews' && (r.reviews.averageRating !== null || r.reviews.paceNote) && (
                          <p class="t-small t-muted">
                            {r.reviews.averageRating !== null && <span>{S.reports.average(r.reviews.averageRating.toFixed(1))}. </span>}
                            {r.reviews.paceNote}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}
              {older && (
                <Section title={S.reports.calls} id="r-calls">
                  <div class="stats">
                    {r.callsAndClicks.map((m) => (
                      <Stat
                        key={m.label}
                        label={m.label}
                        value={m.value}
                        trend={m.lastMonth === null ? null : m.value > m.lastMonth ? 'up' : m.value < m.lastMonth ? 'down' : 'flat'}
                        compare={m.lastMonth === null ? S.home.noLastMonth : S.home.lastMonth(m.lastMonth)}
                        info={<Info title={m.label} lines={[m.note ?? S.app.whereFrom(S.sources[m.source]), ...(m.updatedAt ? [S.app.updated(shortDate(m.updatedAt, zone))] : [])]} />}
                      />
                    ))}
                  </div>
                </Section>
              )}
              {!groups.some((g) => g.group === 'reviews') && (
                <Section title={S.reports.reviews} id="r-reviews">
                  <Card>
                    <p class="t-heading">{S.reports.newReviews(r.reviews.newThisMonth)}</p>
                    {r.reviews.averageRating !== null && <p>{S.reports.average(r.reviews.averageRating.toFixed(1))}</p>}
                    {r.reviews.paceNote && <p class="t-muted">{r.reviews.paceNote}</p>}
                  </Card>
                </Section>
              )}
              <Section title={S.reports.whatWeDid} id="r-did">
                {counts.length > 0 && (
                  <ul class="count-list" aria-label={S.reports.didCounts}>
                    {counts.map((c) => (
                      <li key={c.kind}>
                        <span class="t-data">{c.count}</span> <span>{c.label}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <List>
                  {r.whatWeDid.map((i) => (
                    <LedgerRow key={i.id} item={i} zone={zone} />
                  ))}
                </List>
              </Section>
              <Bullets title={S.reports.yourSide} items={r.yourSide} id="r-side" />
              <Bullets title={S.reports.whatsNext} items={r.whatsNext} id="r-next" />
              <Bullets title={S.reports.notes} items={r.notes} id="r-notes" />
              <div class="btn-row">
                {r.pdfUrl && <Button href={r.pdfUrl} icon="download">{S.reports.download}</Button>}
                <Button
                  icon="share"
                  onClick={async () => {
                    try {
                      const s = await send('reportShare', { params: { month } });
                      await navigator.clipboard?.writeText(s.url).catch(() => undefined);
                      toast(S.reports.shared);
                    } catch {
                      toast(S.errors.offlineSend);
                    }
                  }}
                >
                  {S.reports.share}
                </Button>
              </div>
            </div>
          );
        }}
      </Loaded>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ grids

/** Map runs never change once measured, so a grid read once is kept for the tab: switching views doesn't refetch. */
const gridCache = new Map<string, RankGridData>();
const GRID_CACHE_MAX = 24;

const isGrid = (g: unknown): g is RankGridData => {
  if (!g || typeof g !== 'object') return false;
  const x = g as { size?: unknown; ranks?: unknown };
  return typeof x.size === 'number' && x.size > 0 && Array.isArray(x.ranks);
};

function useGrid(url: string | null): { grid: RankGridData | null; failed: boolean } {
  const [, force] = useState(0);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!url || gridCache.has(url)) return;
    let live = true;
    void getTransport()(url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? (r.json() as Promise<unknown>) : Promise.reject(new Error(String(r.status)))))
      .then(
        (g) => {
          if (!isGrid(g)) throw new Error('not a grid');
          if (gridCache.size >= GRID_CACHE_MAX) gridCache.delete(gridCache.keys().next().value as string);
          gridCache.set(url, g);
          if (live) force((n) => n + 1);
        },
      )
      .catch(() => live && setFailedUrl(url));
    return () => {
      live = false;
    };
  }, [url]);
  return { grid: url ? (gridCache.get(url) ?? null) : null, failed: !!url && failedUrl === url && !gridCache.has(url) };
}

function GridLoading(): JSX.Element {
  return <div class="rank-grid rank-grid-loading" aria-hidden="true" />;
}

function GridFromUrl({ url, compact }: { url: string; compact?: boolean }): JSX.Element {
  const { grid, failed } = useGrid(url);
  if (failed) return <p class="t-muted">{S.app.errorTitle}</p>;
  return grid ? <RankGrid grid={grid} compact={compact} /> : <GridLoading />;
}

/** `compact` draws the bands without the rank in each cell or the legend, for a small card with its number in words. */
export function RankGrid({ grid, compact }: { grid: RankGridData; compact?: boolean }): JSX.Element {
  const size = grid.size;
  const center = Math.floor((size * size) / 2);
  return (
    <figure class="rank-figure">
      <div class="rank-grid" style={`--n:${size}`} data-compact={compact ? '' : undefined} aria-hidden="true">
        {grid.ranks.map((r, i) => (
          <span key={i} class="rank-cell" data-band={band(r)} data-center={i === center ? '' : undefined}>
            {compact ? '' : (r ?? '')}
          </span>
        ))}
      </div>
      {!compact && (
        <figcaption class="rank-legend">
          {(['top3', 'near', 'far', 'none'] as Band[]).map((b) => (
            <span key={b}>
              <i data-band={b} aria-hidden="true" />
              {S.reports.legend[b]}
            </span>
          ))}
        </figcaption>
      )}
    </figure>
  );
}

function RankTable({ grid }: { grid: RankGridData }): JSX.Element {
  const rows = rowsOf(grid.ranks, grid.size);
  return (
    <details class="rank-details">
      <summary>{S.reports.mapTable}</summary>
      <div class="table-scroll">
        <table class="rank-table">
          <caption>{S.reports.mapTableCaption(grid.keyword, longDate(`${grid.measuredAt}T12:00:00Z`, 'UTC'))}</caption>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((r, ci) => (
                  <td key={ci} data-band={band(r)} aria-label={`${S.reports.point(ri + 1, ci + 1)}: ${S.reports.rank(r)}`}>
                    {r ?? '·'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

const CHANGE_KINDS: ChangeCell[] = ['gained', 'lost', 'kept', 'same'];

/** The change map: every point marked gained, lost, kept or the same, each with its own symbol beside its colour. */
function ChangeGrid({ cells, size, label }: { cells: ChangeCell[]; size: number; label: string }): JSX.Element {
  return (
    <figure class="rank-figure">
      <div class="rank-grid" style={`--n:${size}`} role="img" aria-label={label}>
        {cells.map((c, i) => (
          <span key={i} class="rank-cell change-cell" data-change={c} title={S.reports.changeCell[c]}>
            {CHANGE_SYMBOL[c]}
          </span>
        ))}
      </div>
      <figcaption class="rank-legend change-legend">
        {CHANGE_KINDS.map((k) => (
          <span key={k}>
            <i class="change-key" data-change={k} aria-hidden="true">
              {CHANGE_SYMBOL[k]}
            </i>
            {S.reports.changeLegend[k]}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

function ChangeTable({ cells, size, keyword }: { cells: ChangeCell[]; size: number; keyword: string }): JSX.Element {
  return (
    <details class="rank-details">
      <summary>{S.reports.changeTable}</summary>
      <div class="table-scroll">
        <table class="rank-table">
          <caption>{S.reports.changeTableCaption(keyword)}</caption>
          <tbody>
            {rowsOf(cells, size).map((row, ri) => (
              <tr key={ri}>
                {row.map((c, ci) => (
                  <td key={ci} class="change-cell" data-change={c} aria-label={`${S.reports.point(ri + 1, ci + 1)}: ${S.reports.changeCell[c]}`}>
                    {CHANGE_SYMBOL[c]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

// ------------------------------------------------------------------------------------------ the map (S81)

/** One reading: day one or the latest scan, with its number in words, its map and the table. */
function SnapshotView({ snap, waiting }: { snap: MapSnapshot | null; waiting: string }): JSX.Element {
  const { grid, failed } = useGrid(snap?.pointsUrl ?? null);
  if (!snap) return <p class="t-muted">{waiting}</p>;
  return (
    <>
      <p>
        <strong>{S.reports.topThreeOn(points(snap.top3Points))}</strong> <span class="t-small t-muted">{S.reports.scannedOn(dayLabel(snap.day))}</span>
      </p>
      {snap.scans === 2 && <p class="t-small t-muted">{S.reports.pairNote}</p>}
      {failed ? (
        <p class="t-muted">{S.app.errorTitle}</p>
      ) : grid ? (
        <>
          <RankGrid grid={grid} />
          <RankTable grid={grid} />
        </>
      ) : (
        <GridLoading />
      )}
    </>
  );
}

/** Day one against the latest scan, point by point. */
function ChangeView({ svc }: { svc: MapService }): JSX.Element {
  const both = !!svc.baseline && !!svc.latest && svc.baseline.gridRunId !== svc.latest.gridRunId;
  const base = useGrid(both ? svc.baseline!.pointsUrl : null);
  const now = useGrid(both ? svc.latest!.pointsUrl : null);
  if (!both) return <p class="t-muted">{S.reports.changeWaiting}</p>;
  if (base.failed || now.failed) return <p class="t-muted">{S.app.errorTitle}</p>;
  if (!base.grid || !now.grid) return <GridLoading />;
  const cells = base.grid.size === now.grid.size ? changeCells(base.grid.ranks, now.grid.ranks) : null;
  if (!cells) return <p class="t-muted">{S.reports.changeUnreadable}</p>;
  const c = changeCounts(cells);
  const summary = changeSummary(c);
  const tally = S.reports.changeCounts(c.gained, c.lost, c.kept);
  const movement = svc.change?.movement ?? null;
  return (
    <>
      <p>
        <strong>{summary}</strong>
      </p>
      <p class="cluster t-small t-muted">
        {movement && <MovementChip movement={movement} />}
        <span>{tally}</span>
        {movement && <Info title={S.reports.movementTitle} lines={[S.reports.movementInfo]} />}
      </p>
      {(svc.baseline!.scans === 2 || svc.latest!.scans === 2) && <p class="t-small t-muted">{S.reports.pairNote}</p>}
      <ChangeGrid cells={cells} size={base.grid.size} label={`${summary}. ${tally}`} />
      <ChangeTable cells={cells} size={base.grid.size} keyword={svc.keyword} />
    </>
  );
}

/** Every scan of one service, back to day one, with the ledger under it (no causal wording). */
function ScanSeries({ service, zone }: { service: string; zone: string }): JSX.Element {
  const q = useQuery('reportMap', { params: { service } });
  const [idx, setIdx] = useState<number | null>(null);
  useEffect(() => setIdx(null), [service]);
  const runs = q.data?.runs ?? [];
  const i = Math.min(idx ?? runs.length - 1, runs.length - 1);
  const run = runs[i];
  const { grid, failed } = useGrid(run?.pointsUrl ?? null);
  return (
    <Section title={S.reports.everyScan} id="map-series">
      <Loaded q={q} skeleton="cards">
        {(d) => (
          <div class="stack" data-gap="l">
            {run && (
              <div class="map-stepper">
                <button type="button" class="icon-btn icon-only" aria-label={S.reports.mapEarlier} disabled={i <= 0} onClick={() => setIdx(i - 1)}>
                  <Icon name="chevron-left" />
                </button>
                <div class="map-stepper-mid">
                  <input
                    type="range"
                    min={0}
                    max={Math.max(0, runs.length - 1)}
                    value={i}
                    aria-label={S.reports.mapMonth}
                    aria-valuetext={`${run.purpose === 'baseline' ? S.reports.mapBaseline : shortDate(run.runAt, zone)}: ${S.reports.mapOf(run.top3 ?? 0)}`}
                    onInput={(e) => setIdx(Number((e.currentTarget as HTMLInputElement).value))}
                  />
                  <p aria-live="polite">
                    <strong>{run.purpose === 'baseline' ? S.reports.mapBaseline : shortDate(run.runAt, zone)}</strong> · {S.reports.mapOf(run.top3 ?? 0)}
                  </p>
                </div>
                <button type="button" class="icon-btn icon-only" aria-label={S.reports.mapLater} disabled={i >= runs.length - 1} onClick={() => setIdx(i + 1)}>
                  <Icon name="chevron-right" />
                </button>
              </div>
            )}
            {failed ? (
              <p class="t-muted">{S.app.errorTitle}</p>
            ) : grid ? (
              <>
                <RankGrid grid={grid} />
                <RankTable grid={grid} />
              </>
            ) : run?.pointsUrl ? (
              <GridLoading />
            ) : null}
            <section class="section" aria-labelledby="map-ledger">
              <h3 id="map-ledger">{S.reports.ledger}</h3>
              <p class="t-small t-muted">{S.reports.ledgerNote}</p>
              <List>
                {d.ledger.map((l) => (
                  <LedgerRow key={l.id} item={l} zone={zone} />
                ))}
              </List>
            </section>
          </div>
        )}
      </Loaded>
    </Section>
  );
}

function MapsBody({ d, zone }: { d: MapsData; zone: string }): JSX.Element {
  const [slug, setSlug] = useState<string | null>(null);
  const [picked, setPicked] = useState<MapView | null>(null);
  const svc = d.services.find((s) => s.serviceSlug === slug) ?? d.services[0];
  const nextScan = d.nextScanOn ? <p class="t-muted">{S.reports.nextScan(dayLabel(d.nextScanOn))}</p> : null;
  // Empty: no service has its day one map yet.
  if (!svc || d.services.every((s) => !s.baseline)) {
    return <EmptyState title={S.reports.mapEmptyTitle} body={d.nextScanOn ? `${S.reports.mapEmptyBody} ${S.reports.nextScan(dayLabel(d.nextScanOn))}` : S.reports.mapEmptyBody} />;
  }
  const view: MapView = picked ?? (svc.latest ? 'latest' : 'baseline');
  return (
    <div class="stack" data-gap="l">
      {d.services.length > 1 ? (
        <SelectField label={S.reports.service} value={svc.serviceSlug} onChange={(v) => setSlug(v || null)} options={d.services.map((s) => ({ value: s.serviceSlug, label: s.keyword }))} />
      ) : (
        <p class="t-small t-muted">"{svc.keyword}"</p>
      )}
      <section class="stack" data-gap="m" aria-label={`${S.reports.mapTitle}: ${svc.keyword}`}>
        <Segmented<MapView>
          label={S.reports.view}
          value={view}
          onChange={setPicked}
          options={[
            { value: 'baseline', label: S.reports.views.baseline! },
            { value: 'latest', label: S.reports.views.latest! },
            { value: 'change', label: S.reports.views.change! },
          ]}
        />
        <div class="stack" data-gap="m">
          {view === 'baseline' && <SnapshotView key={`b-${svc.serviceSlug}`} snap={svc.baseline} waiting={S.reports.mapEmptyBody} />}
          {view === 'latest' && <SnapshotView key={`l-${svc.serviceSlug}`} snap={svc.latest} waiting={S.reports.latestWaiting} />}
          {view === 'change' && <ChangeView key={`c-${svc.serviceSlug}`} svc={svc} />}
        </div>
      </section>
      <div class="stack" data-gap="s">
        {/* The grid, as recorded at day one (c011), and when the next scan is. */}
        {d.grid && <p class="t-small t-muted">{S.reports.gridFacts(S.facts.miles(String(d.grid.spacingMiles)), dayLabel(d.grid.recordedOn, true))}</p>}
        {nextScan}
      </div>
      <ScanSeries service={svc.serviceSlug} zone={zone} />
    </div>
  );
}

export function MapScreen(): JSX.Element {
  const zone = useStore(tz);
  const loc = useStore(location);
  const q = useQuery('reportMaps');
  return (
    <Screen title={S.reports.mapTitle} back={scoped('/', loc.scope)} lead={S.reports.mapLead} query={q}>
      <Loaded q={q} skeleton="cards">
        {(d) => <MapsBody d={d} zone={zone} />}
      </Loaded>
    </Screen>
  );
}

// ------------------------------------------------------------------------------------------ the day 90 read (S82)

export function Day90Screen(): JSX.Element {
  const q = useQuery('reportDay90');
  return (
    <Screen title={S.reports.day90} back="/reports" query={q}>
      <Loaded q={q}>
        {(d) => {
          if (!d.available)
            return (
              <div class="stack">
                {d.availableOn && <p>{S.reports.day90Soon(dayLabel(d.availableOn))}</p>}
                {d.baselineAverage !== null && <p class="t-muted">{S.reports.day90Base(String(d.baselineAverage))}</p>}
              </div>
            );
          const chip = d.outcome ? day90Chip(d.outcome) : null;
          const services = d.services ?? [];
          return (
            <div class="stack" data-gap="l">
              <Card>
                {chip && (
                  <p class="d90-outcome">
                    <Chip tone={chip.tone} icon={chip.tone === 'ok' ? 'check' : 'dot'}>
                      {chip.text}
                    </Chip>
                  </p>
                )}
                <ul class="bullets">
                  {d.explanation.map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
                {(d.lateDays ?? 0) > 0 && <p class="t-small t-muted">{S.day90.late(d.lateDays ?? 0)}</p>}
              </Card>
              {services.length > 0 && (
                <Section title={S.day90.services} id="d90-services">
                  <div class="table-scroll">
                    <table class="d90-table">
                      <caption>{S.day90.servicesCaption}</caption>
                      <thead>
                        <tr>
                          <th scope="col">{S.day90.service}</th>
                          <th scope="col">{S.day90.dayOne}</th>
                          <th scope="col">{S.day90.dayNinety}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {services.map((s) => (
                          <tr key={s.serviceSlug}>
                            <th scope="row">"{s.keyword}"</th>
                            <td>{points(s.baseline)}</td>
                            <td>{points(s.day90)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}
              {d.habit && (
                <Section title={S.day90.habitTitle} id="d90-habit">
                  <p>{S.day90.habit(d.habit.weeksKept, d.habit.monthsKept)}</p>
                  <p class="t-small t-muted">{S.day90.habitNeeds}</p>
                </Section>
              )}
            </div>
          );
        }}
      </Loaded>
    </Screen>
  );
}
